'use client';

import './styles/@theme';
import { getText } from '@/utils/text/text';
import { optInManagerService } from '@/app/components/site-wide-jackpot/hooks/use-opt-in-manager';
import type { SupportLink } from '@/logic/page-config/footer/footer-links';
import { POPUPS, POPUP_ERROR_CODES } from '@/app/components/popup/constants';
import { useStore } from '@/store/store';

export type ErrorPopupProps = {
    onClose: () => void;
    errorCode: string;
    support?: SupportLink;
    multiplier?: number;
};

export const ErrorPopup = ({ onClose, errorCode, support, multiplier }: ErrorPopupProps) => {
    const manager = optInManagerService.getInstance();
    const { queuePopup } = useStore((s) => s.popup);
    const setMultiplier = useStore((s) => s.siteWideJackpot.setMultiplier);
    const isOptingIn = errorCode !== POPUP_ERROR_CODES.OPT_OUT_FAILED;

    const handleCtaClick = () => {
        onClose();
        // Timeout is needed because there is an animation when closing the popup that takes some time to complete
        setTimeout(() => {
            manager.overrideParams({
                callback: (v: number | { [key: string]: boolean }) => {
                    if (typeof v === 'number') {
                        setMultiplier(v as 0 | 1 | 2);
                    }
                },
                errorCallback: (error: { reason: string; message?: string }, errorMultiplier?: number) => {
                    const errorMultiplierToUse = isOptingIn ? (errorMultiplier ?? multiplier) : undefined;

                    queuePopup({
                        type: POPUPS.SWJ_ERROR,
                        params: {
                            errorCode: error.reason,
                            support,
                            multiplier: errorMultiplierToUse
                        }
                    });
                }
            });

            if (errorCode === POPUP_ERROR_CODES.OPT_OUT_FAILED) {
                manager.optOut();
            } else {
                manager.optIn(multiplier);
            }
        }, 800);
    };

    return (
        <div className="site-wide-error-modal">
            <div className="site-wide-error-modal__wrapper">
                <div className="site-wide-error-modal__wrapper__items">
                    <img
                        src={`/images/${process.env.BRAND}/general-error.svg`}
                        className="error-icon"
                        alt="Error Icon"
                    />
                    <div className="content">
                        <div className="error-title">
                            {getText({ text: 'components.siteWideJackpots.errorStatus.title' })}
                        </div>
                        <div className="extended">
                            {getText({ text: 'components.siteWideJackpots.errorStatus.message' })}
                        </div>
                        <div className="buttons">
                            <button type="button" className="navigate-button" onClick={handleCtaClick}>
                                {getText({ text: 'components.siteWideJackpots.errorStatus.cta' })}
                            </button>
                            <button type="button" className="navigate-button-close" onClick={onClose}>
                                {getText({ text: 'components.siteWideJackpots.errorStatus.exit' })}
                            </button>
                        </div>
                    </div>
                    <div className="site-wide-navigation">
                        <div className="help">
                            {getText({ text: 'components.siteWideJackpots.errorStatus.help' })}
                            {support?.url && (
                                <a rel="external" href={support.url} target={support.target} className="support">
                                    {getText({ text: 'components.siteWideJackpots.errorStatus.contact' })}
                                </a>
                            )}
                        </div>
                        {getText({ text: 'components.siteWideJackpots.errorStatus.error', params: [errorCode] })}
                    </div>
                </div>
            </div>
        </div>
    );
};
